import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import getBreakpointField, { breakpointOptions } from "@store/Framework/Common/Fields/Toolkit/breakpoint";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import { getDataOptionsFieldDefinition } from "@store/Framework/Common/Utilities/dataOptions";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import Carousel, {
    CarouselAlign,
    CarouselArrowsPositionVariant,
    carouselArrowsPositionVariantOptions,
    carouselDefaultCustomCss,
    CarouselItemsAlign,
    CarouselItemsJustify,
    CarouselPresentationProps,
    CarouselSlide,
    getItemsPerSlideFromFieldValue,
} from "@store/Overrides/Components/Carousel";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    align = "align",
    arrowsGap = "arrowsGap",
    arrowsPositionVariant = "arrowsPositionVariant",
    autoplay = "autoplay",
    autoplayDelay = "autoplayDelay",
    customCSS = "customCSS",
    inlineArrows = "inlineArrows",
    inlineArrowsBreakpoint = "inlineArrowsBreakpoint",
    invertColor = "invertColor",
    items = "items",
    itemsAlign = "itemsAlign",
    itemsGap = "itemsGap",
    itemsJustify = "itemsJustify",
    itemsPerSlide = "itemsPerSlide",
    itemsToScroll = "itemsToScroll",
    keepInvisibleArrows = "keepInvisibleArrows",
    loop = "loop",
    showArrows = "showArrows",
    showDots = "showDots",
}

const enum itemFields {
    id = "id",
    name = "name",
}

export interface CarouselWidgetPresentationProps extends ExpandStyles<CarouselWidgetStyles> {}

export interface CarouselWidgetProps extends CarouselWidgetPresentationProps, WidgetProps {
    fields: {
        [fields.align]: CarouselAlign;
        [fields.arrowsGap]: string;
        [fields.autoplay]: boolean;
        [fields.autoplayDelay]: number;
        [fields.arrowsPositionVariant]: CarouselArrowsPositionVariant;
        [fields.customCSS]: string;
        [fields.inlineArrows]: boolean;
        [fields.inlineArrowsBreakpoint]: BreakpointKey;
        [fields.invertColor]: boolean;
        [fields.items]: [
            {
                fields: {
                    [itemFields.id]: string;
                    [itemFields.name]: string;
                };
            },
        ];
        [fields.itemsAlign]: CarouselItemsAlign;
        [fields.itemsGap]: string;
        [fields.itemsJustify]: CarouselItemsJustify;
        [fields.itemsPerSlide]: string;
        [fields.itemsToScroll]: string;
        [fields.keepInvisibleArrows]: boolean;
        [fields.loop]: boolean;
        [fields.showArrows]: boolean;
        [fields.showDots]: boolean;
    };
}

const arrowsGapField = getCssUnitField(fields.arrowsGap);
const inlineArrowsBreakpointField = getBreakpointField(fields.inlineArrowsBreakpoint);
const itemsGapField = getCssUnitField(fields.itemsGap);
const itemsPerSlideField = getNumberTextField(fields.itemsPerSlide, { integer: true, min: 1 });
const itemsToScrollField = getNumberTextField(fields.itemsToScroll, { integer: true, min: 1 });

export interface CarouselWidgetStyles {
    carousel?: CarouselPresentationProps;
}

const carouselWidgetStyles: CarouselWidgetStyles = {};

const styles = carouselWidgetStyles;

const CarouselWidget: FC<CarouselWidgetProps> = ({
    fields: {
        align,
        arrowsGap: baseArrowsGap,
        autoplay,
        autoplayDelay,
        arrowsPositionVariant,
        customCSS,
        inlineArrows,
        inlineArrowsBreakpoint: baseInlineArrowsBreakpoint,
        invertColor,
        items,
        itemsAlign,
        itemsGap: baseItemsGap,
        itemsJustify,
        itemsPerSlide: baseItemsPerSlide,
        itemsToScroll: baseItemsToScroll,
        keepInvisibleArrows,
        loop,
        showArrows,
        showDots,
    },
    id,
}) => {
    const arrowsGap = arrowsGapField.useParse(baseArrowsGap);
    const inlineArrowsBreakpoint = inlineArrowsBreakpointField.useParse(baseInlineArrowsBreakpoint);
    const itemsGap = itemsGapField.useParse(baseItemsGap) || 0;
    const itemsPerSlide = getItemsPerSlideFromFieldValue(baseItemsPerSlide, itemsPerSlideField.parse);
    const itemsToScroll = itemsToScrollField.useParse(baseItemsToScroll);
    const { isEditing } = useShellContext();

    const carouselStyles = useMemo(() => {
        return mergeProps<CarouselWidgetStyles["carousel"]>(styles.carousel, {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${customCSS}
                    `,
                },
            },
        });
    }, [customCSS]);

    if (!items.length && !isEditing) {
        return null;
    }

    const slides: CarouselSlide[] = items.map((item, index) => ({
        id: `item-${id}-${item.fields.id || index}`,
        renderComponent: () => <Zone contentId={id} zoneName={`Item-${item.fields.id || index}`} />,
    }));

    return (
        <Carousel
            {...carouselStyles}
            // title={title}
            align={align}
            maxNumberOfColumns={1}
            arrowsGap={arrowsGap}
            arrowsPositionVariant={arrowsPositionVariant}
            autoplay={autoplay}
            autoplayDelay={autoplayDelay * 1000}
            id={`carousel-${id}`}
            inlineArrowsBreakpoint={inlineArrows ? inlineArrowsBreakpoint : undefined}
            invertColor={invertColor}
            itemsAlign={itemsAlign}
            itemsGap={itemsGap}
            itemsJustify={itemsJustify}
            itemsPerSlide={itemsPerSlide}
            itemsToScroll={itemsToScroll}
            keepInvisibleArrows={keepInvisibleArrows}
            loop={loop}
            showArrows={showArrows}
            showDots={showDots}
            slides={slides}
        />
    );
};

const defaultCustomCss = `&${carouselDefaultCustomCss}`;

const advancedTab = { displayName: "Advanced", sortOrder: 1 };

// eslint-disable-next-line no-undef
const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: CarouselWidget,
    definition: {
        displayName: "Carousel",
        group: "Basic",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                displayName: "Show Arrows",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                ...arrowsGapField.definition,
                displayName: "Arrows gap",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: x =>
                    x.fields.showArrows &&
                    (!x.fields.inlineArrows || x.fields.inlineArrowsBreakpoint !== breakpointOptions.xl.id),
            },
            {
                name: fields.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.autoplay,
                displayName: "Enable Autoplay",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
            {
                name: fields.autoplayDelay,
                displayName: "Autoplay delay",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: 8,
                min: 1,
                isVisible: x => x.fields.autoplay,
                tooltip:
                    "The number of seconds to wait before changing slide, autoplay will remain disabled while editing",
            },
            {
                ...itemsPerSlideField.definition,
                displayName: "Items per slide",
                fieldType: "Contextual",
                defaultValue: "1",
            },
            {
                ...itemsToScrollField.definition,
                displayName: "Items to scroll",
                fieldType: "Contextual",
                defaultValue: "",
                tooltip: `${
                    itemsToScrollField.definition.tooltip ? `${itemsToScrollField.definition.tooltip} ` : ""
                } Determines how many items will scroll when changing slide, matches the "Items per slide" setting if empty`,
            },
            {
                ...itemsGapField.definition,
                displayName: "Items gap",
                fieldType: "Contextual",
                defaultValue: "",
            },
            {
                name: fields.items,
                getDisplay: x => x.fields.name || translate("Item"),
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: itemFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: itemFields.name,
                        displayName: "Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.loop,
                displayName: "Loop",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
                tab: advancedTab,
            },
            {
                name: fields.invertColor,
                displayName: `Invert controls color`,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tooltip: "The color may not change if it is already suited for both light and dark backgrounds",
                tab: advancedTab,
            },
            {
                name: fields.inlineArrows,
                displayName: "Display arrows below the carousel",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: x => x.fields.showArrows,
                tab: advancedTab,
            },
            {
                ...inlineArrowsBreakpointField.definition,
                displayName: "Display arrows below the carousel (breakpoint)",
                fieldType: "Contextual",
                defaultValue: breakpointOptions.xl.id,
                hideEmptyOption: true,
                tooltip: "The arrows will be displayed under the carousel until the selected breakpoint is passed",
                tooltipPosition: "left",
                isVisible: x => x.fields.showArrows && x.fields.inlineArrows,
                tab: advancedTab,
            },
            {
                ...getDataOptionsFieldDefinition(carouselArrowsPositionVariantOptions),
                name: fields.arrowsPositionVariant,
                displayName: "Side arrows position variant",
                fieldType: "Contextual",
                hideEmptyOption: true,
                isVisible: x =>
                    x.fields.showArrows &&
                    (!x.fields.inlineArrows || x.fields.inlineArrowsBreakpoint !== breakpointOptions.xl.id),
                tab: advancedTab,
            },
            {
                name: fields.keepInvisibleArrows,
                displayName: "Keep spacing of side arrows when hidden",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: x =>
                    x.fields.showArrows &&
                    (!x.fields.inlineArrows || x.fields.inlineArrowsBreakpoint !== breakpointOptions.xl.id),
                tab: advancedTab,
            },
            {
                name: fields.align,
                displayName: "Items Alignment",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Start", value: "start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "End", value: "end" },
                ],
                defaultValue: "start",
                hideEmptyOption: true,
                tooltip: `Aligns the items in the carousel viewport, mostly visible when the "Items to scroll" setting is different than the "Items per slide" setting`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                name: fields.itemsAlign,
                displayName: "Content Alignment",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Start", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "End", value: "flex-end" },
                    { displayName: "Stretch", value: "stretch" },
                ],
                defaultValue: "flex-start",
                hideEmptyOption: true,
                tooltip: `Aligns the content of an item relative to other items, only visible when contents have different sizes`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                name: fields.itemsJustify,
                displayName: "Slide Alignment",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Start", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "End", value: "flex-end" },
                ],
                defaultValue: "flex-start",
                hideEmptyOption: true,
                tooltip: `Aligns the slide, only visible when there is not enough items to fulfill the "Items per slide" setting`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
